import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QuantityMode, QuantityFormula } from "./useEstimates";

export interface TemplateSubLine {
  type: "labor" | "material";
  description: string;
  unit: string;
  quantity_mode: QuantityMode;
  quantity_formula?: QuantityFormula | null;
  default_markup_pct?: number;
  sort_order: number;
}

export interface TemplateLineItem {
  description: string;
  unit: string;
  sub_lines: TemplateSubLine[];
  group_id?: string | null;
  takeoff_source?: {
    measurement_type: "linear" | "area" | "count";
    group_match?: "by_name" | "by_id";
    group_name?: string;
  };
  /** @deprecated legacy read-only — auto-converted to sub_lines on read */
  sub_line_types?: ("labor" | "material")[];
}

export interface TemplateGroup {
  id: string;
  name: string;
  group_type: "room" | "phase" | "trade" | "custom";
  sort_order: number;
  color?: string;
}

export interface EstimateTemplate {
  id: string;
  company_id: string;
  name: string;
  category: string;
  line_items: TemplateLineItem[];
  template_groups?: TemplateGroup[];
  labor_markup_pct: number;
  material_markup_pct: number;
  created_at: string;
  updated_at: string;
}

/**
 * Convert legacy sub_line_types to sub_lines on read.
 * If sub_lines already exists, use it as-is.
 */
function normalizeTemplateLineItem(tli: any, parentUnit?: string): TemplateLineItem {
  if (Array.isArray(tli.sub_lines) && tli.sub_lines.length > 0) {
    return {
      description: tli.description ?? "",
      unit: tli.unit ?? "SF",
      sub_lines: tli.sub_lines.map((sl: any, i: number) => ({
        type: sl.type ?? "labor",
        description: sl.description ?? "",
        unit: sl.unit ?? tli.unit ?? "SF",
        quantity_mode: (sl.quantity_mode as QuantityMode) ?? "auto",
        quantity_formula: sl.quantity_formula ?? null,
        default_markup_pct: sl.default_markup_pct,
        sort_order: sl.sort_order ?? i,
      })),
    };
  }
  // Legacy: convert sub_line_types to sub_lines
  const types: ("labor" | "material")[] = Array.isArray(tli.sub_line_types) ? tli.sub_line_types : [];
  return {
    description: tli.description ?? "",
    unit: tli.unit ?? "SF",
    sub_lines: types.map((t, i) => ({
      type: t,
      description: "",
      unit: tli.unit ?? "SF",
      quantity_mode: "auto" as QuantityMode,
      quantity_formula: null,
      sort_order: i,
    })),
  };
}

export function useEstimateTemplates() {
  return useQuery<EstimateTemplate[]>({
    queryKey: ["estimate_templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimate_templates")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        ...d,
        line_items: (d.line_items ?? []).map((tli: any) => normalizeTemplateLineItem(tli)),
      }));
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<EstimateTemplate, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("estimate_templates")
        .insert({
          company_id: input.company_id,
          name: input.name,
          category: input.category,
          line_items: input.line_items as any,
          labor_markup_pct: input.labor_markup_pct,
          material_markup_pct: input.material_markup_pct,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<EstimateTemplate> & { id: string }) => {
      const updateObj: Record<string, any> = {};
      if (input.name !== undefined) updateObj.name = input.name;
      if (input.category !== undefined) updateObj.category = input.category;
      if (input.line_items !== undefined) updateObj.line_items = input.line_items as any;
      if (input.labor_markup_pct !== undefined) updateObj.labor_markup_pct = input.labor_markup_pct;
      if (input.material_markup_pct !== undefined) updateObj.material_markup_pct = input.material_markup_pct;
      const { data, error } = await supabase
        .from("estimate_templates")
        .update(updateObj)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("estimate_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}
